import { MinusOutlined, PlusOutlined } from "@ant-design/icons";
import { useTranslation } from "react-i18next";
import { useMenuItems } from "../../../../hooks/useMenu";
import type { Addon } from "../../../../api/menu";
import { getItemName, getTranslation } from "../../../../utils/translations";
import { AddonCheckbox } from "./AddonCheckbox";
import type { AddonSelection, AddonSelectionState } from "./useAddonSelection";

// Маха опцията от групата; празната група изчезва от избора изцяло.
const withoutOption = (
  selection: AddonSelection,
  addonId: number,
  itemId: number,
): AddonSelection => {
  const { [itemId]: _, ...restItems } = selection[addonId] || {};
  void _;
  if (Object.keys(restItems).length === 0) {
    const { [addonId]: __, ...rest } = selection;
    void __;
    return rest;
  }
  return { ...selection, [addonId]: restItems };
};

/**
 * Добавките в „Промени" (EditModifiersModal) — вертикален списък от групи с
 * редове (скролът идва от тялото на модала).
 */
export const AddonGroupsSection = ({
  selection,
}: {
  selection: AddonSelectionState;
}) => {
  const {
    addons,
    selectedAddonItems,
    setSelectedAddonItems,
    selectedCountOf,
    requiredCountOf,
  } = selection;

  return addons.map((addon) => (
    <AddonGroup
      key={addon.id}
      addon={addon}
      selectedCount={selectedCountOf(addon.id)}
      requiredCount={requiredCountOf(addon)}
      quantityByItemId={selectedAddonItems[addon.id] || {}}
      onToggle={(itemId, isSelected) =>
        setSelectedAddonItems((previous) =>
          isSelected
            ? withoutOption(previous, addon.id, itemId)
            : {
                ...previous,
                [addon.id]: { ...(previous[addon.id] || {}), [itemId]: 1 },
              },
        )
      }
      onQuantityChange={(itemId, delta) =>
        setSelectedAddonItems((previous) => {
          const newQuantity = (previous[addon.id]?.[itemId] || 0) + delta;
          // Слизане под 1 = премахване на добавката
          if (newQuantity <= 0) return withoutOption(previous, addon.id, itemId);
          return {
            ...previous,
            [addon.id]: { ...(previous[addon.id] || {}), [itemId]: newQuantity },
          };
        })
      }
    />
  ));
};

interface AddonGroupProps {
  addon: Addon;
  selectedCount: number;
  requiredCount: number;
  quantityByItemId: Record<number, number>;
  onToggle: (itemId: number, isSelected: boolean) => void;
  onQuantityChange: (itemId: number, delta: number) => void;
}

// Една група: заглавие с бадж + редовете на опциите. Тип "1" (обикновена) има
// брояч +/− на реда; тип "2"/"3" са само чекбокс.
const AddonGroup = ({
  addon,
  selectedCount,
  requiredCount,
  quantityByItemId,
  onToggle,
  onQuantityChange,
}: AddonGroupProps) => {
  const { t, i18n } = useTranslation();
  const allItems = useMenuItems();

  // Само опциите, които реално са в менюто на ресторанта
  const options = addon.items.flatMap((addonItem) => {
    const menuItem = allItems.find((item) => item.id === addonItem.itemId);
    return menuItem ? [{ addonItem, menuItem }] : [];
  });
  if (options.length === 0) return null;

  const required = addon.type === 2;
  const recommended = addon.type === 3;
  const withQuantity = addon.type === 1;
  const maxQty = addon.maxQty || 999;

  return (
    <div className="flex flex-col gap-2.5">
      <div className="flex items-center justify-between gap-2">
        <span className="inline-flex items-center gap-2 font-pos-mono text-[11px] font-semibold uppercase tracking-[.1em] text-pos-dim">
          {getTranslation(addon.translations, i18n.language, addon.slug)}
          {/* Бадж на групата: изберете (задълж.) / препоръчани / по избор */}
          <span
            className={`rounded-full px-2.5 py-0.5 text-[11px] font-semibold normal-case tracking-normal ${
              required
                ? "bg-pos-ready-bg text-pos-ready"
                : recommended
                  ? "bg-pos-marked text-pos-accent"
                  : "bg-pos-rowline text-pos-muted"
            }`}
          >
            {required
              ? t("ordering.addonRequired")
              : recommended
                ? t("ordering.addonRecommended")
                : t("ordering.addonOptional")}
          </span>
        </span>
        {required && (
          <span className="font-pos-mono text-[11px] text-pos-muted">
            {t("common.selected")}: {selectedCount} / {requiredCount}
          </span>
        )}
      </div>

      <div className="flex flex-col gap-1.5">
        {options.map(({ addonItem, menuItem }) => {
          const itemQuantity = quantityByItemId[addonItem.itemId] || 0;
          const isSelected = itemQuantity > 0;
          // maxQty ограничава избора САМО при задължителна добавка (тип 2);
          // обикновените и препоръчителните нямат лимит.
          const canSelect = !required || selectedCount < maxQty || isSelected;
          const price = addonItem.addonItemPrice ?? addonItem.price ?? 0;
          const blocked = !canSelect && !isSelected;

          // Редът е div, не button — вътре има бутони на брояча, а button в
          // button е невалиден HTML. Затова Tab/Space идват от role="checkbox"
          // + tabIndex, за да е достижим с клавиатурата като модификаторите.
          // Enter НЕ се пипа тук: глобалният handler на модала го прави
          // потвърждение.
          return (
            <div
              key={addonItem.itemId}
              role="checkbox"
              aria-checked={isSelected}
              aria-disabled={blocked}
              tabIndex={blocked ? -1 : 0}
              onClick={() => {
                if (blocked) return;
                onToggle(addonItem.itemId, isSelected);
              }}
              onKeyDown={(e) => {
                if (e.key !== " " || e.target !== e.currentTarget) return;
                // Space иначе скролва тялото на модала
                e.preventDefault();
                if (blocked) return;
                onToggle(addonItem.itemId, isSelected);
              }}
              className={`flex items-center gap-3 rounded-xl border px-3.5 py-[11px] transition-colors ${
                isSelected
                  ? "border-pos-accent bg-pos-marked"
                  : "border-pos-line3 bg-pos-card"
              } ${blocked ? "cursor-not-allowed opacity-45" : "cursor-pointer"}`}
            >
              <AddonCheckbox checked={isSelected} />

              <span className="min-w-0 flex-1 truncate text-left text-sm font-semibold text-pos-ink">
                {getItemName(menuItem.itemLangs, i18n.language, menuItem.slug)}
              </span>

              {/* Цена (единична) */}
              <span className="flex-shrink-0 font-pos-mono text-[13px] font-semibold text-pos-accent-ink">
                +{price.toFixed(2)}
              </span>

              {/* Брояч — само при обикновена добавка (тип "1") и избран ред */}
              {withQuantity && isSelected && (
                <span
                  onClick={(e) => e.stopPropagation()}
                  className="inline-flex flex-shrink-0 items-stretch overflow-hidden rounded-[10px] border border-pos-ctl bg-pos-card"
                >
                  <button
                    type="button"
                    onClick={() => onQuantityChange(addonItem.itemId, -1)}
                    className="grid h-8 w-8 cursor-pointer place-items-center bg-pos-stepper text-pos-dim"
                  >
                    <MinusOutlined className="text-[11px]" />
                  </button>
                  <span className="grid min-w-[34px] place-items-center font-pos-mono text-[15px] font-semibold text-pos-ink">
                    {itemQuantity}
                  </span>
                  <button
                    type="button"
                    onClick={() => onQuantityChange(addonItem.itemId, 1)}
                    className="grid h-8 w-8 cursor-pointer place-items-center bg-pos-stepper text-pos-accent-ink"
                  >
                    <PlusOutlined className="text-[11px]" />
                  </button>
                </span>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
};
